import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { showAlert, changeFormatWithoutTz, customStylesLight } from '../../../../../../Assests/Utility';
import { AppConstant } from '../../../../../../Assests/AppConstant';
import { sagaActions } from '../../../../../../../sagas/sagaActions';
import { getCurrentDate } from '../../../../../../../Utils/DateUtils';
import { getUserSelector } from '../../../../../../../redux/reducers/reducerSlices/UserReducer';
import { getWhMetaDataReducerSlice } from '../../../../../../../redux/reducers/reducerSlices/WhMetaDataReducerSlice';
import moment from 'moment';
import SSPopup from '../../../../../Common/Popup/SSPopup/SSPopup';
import WarehouseSearchSS from '../../../../../SuperWarehouse/Components/SearchWarehouse/WarehouseSearchSS';
import CustomFieldWithTtileSS from '../../../../../Common/CustomField/CustomFieldWithTtileSS';
import CustomButtonSS from '../../../../../Common/CustomButton/CustomButtonSS';
import { smartScheduleSelector } from '../../../../../../../redux/reducers/reducerSlices/SmartScheduleReducer';
import StandingAppointmentsList from './StandingAppointmentsList';
import { CustomDynamicFormSS } from '../../../../../Common/CustomForm/CustomDynamicFormSS';
import { fetchDockListCommonFunc } from '../DocksMeta/Common/FetchDockList';
import { fetchTimeslotListCommonFunc } from '../DockDoorsForm/Common/FetchTimeslotsList';
import AutomaticCancelPopup from '../Common/AutomaticCancelPopup';
import { useNavigate } from 'react-router-dom';
import CustomTooltip from '../../../../../Common/Tooltip/CustomTooltip';

// today in YYYY-MM-DD for native date input min
const todayStr = moment().format('YYYY-MM-DD');

/**
 * Validates a YYYY-MM-DD string coming from a native <input type="date">.
 * @param {string} dateStr   - e.target.value ('' when invalid/incomplete)
 * @param {boolean} badInput - e.target.validity.badInput
 * @param {'start'|'end'} fieldRole - for past-date check context
 * @param {string|null} minDateStr  - YYYY-MM-DD; dateStr must be >= minDateStr when provided
 * @returns {string} error message or '' when valid
 */
const validateDateInput = (dateStr, badInput, fieldRole, minDateStr) => {
  if (badInput && !dateStr) {
    return 'Please enter a valid date (MM/DD/YYYY)';
  }

  if (!dateStr) return '';

  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const year = parts[0];
    if (year.length !== 4 || parseInt(year, 10) < 1000 || parseInt(year, 10) > 9999) {
      return 'Year must be a valid 4-digit year (e.g. 2025)';
    }

    // Validate calendar correctness: e.g. Feb 31 is invalid
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    const dateObj = new Date(`${dateStr}T00:00:00`);
    if (
      isNaN(dateObj.getTime()) ||
      dateObj.getMonth() + 1 !== month ||
      dateObj.getDate() !== day
    ) {
      return `Invalid date: ${parts[1]}/${parts[2]}/${year} does not exist`;
    }
  }

  if (fieldRole === 'start' && minDateStr && dateStr < minDateStr) {
    return 'Start Date cannot be a past date';
  }

  return '';
};

/**
 * Derives DurationMins value and editability based on CapacityModeling type.
 * - Productivity → editable, no auto-set
 * - TPH          → fixed at 60, not editable
 * - TPM        → fixed at TPM value, not editable
 *
 * @param {object|null} dock - dock object from dockList
 * @returns {{ durationMins: number|null, isDurationEditable: boolean }}
 */
const getDurationFromCapacityModeling = (dock) => {
  if (!dock) return { durationMins: null, isDurationEditable: true };
  const modeling = dock.CapacityModeling;
  if (modeling === 'TPH') {
    return { durationMins: 60, isDurationEditable: false };
  }
  if (modeling === 'TPM') {
    const tpm = dock.TPM != null ? Number(dock.TPM) : null;
    return { durationMins: tpm, isDurationEditable: false };
  }
  // Productivity or any other value — user editable
  return { durationMins: null, isDurationEditable: true };
};

/**
 * Returns a display label for CapacityModeling value.
 */
const getCapacityModelingLabel = (modeling) => {
  if (modeling === 'TPH') return 'Trucks Per Hour (TPH)';
  if (modeling === 'TPM') return 'Trucks Per Duration (TPD)';
  if (modeling === 'Productivity') return 'Productivity';
  return modeling || '';
};

const StandingAppointmentsBlock = (props) => {
  const { themeSelected, whObj } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [searchStr, setSearchStr] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [inputs, setInputs] = useState({
    IDCarrier: null,
    IDDock: null,
    selectedDoors: [],
    Frequency: 'DAILY',
    IsActive: true,
    WeeklyData: {
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
    },
    ExtendRule: false,
    StartDateTime: '',
    EndDateTime: '',
    StandingBlockLeadTime: '',
    AllowOtherCarriersOverSAB: false,
  });
  const [errors, setErrors] = useState({});
  const [carrierListState, setCarrierListState] = useState([]);
  const [dockList, setDockList] = useState([]);
  // Tracks whether DurationMins field is editable (driven by CapacityModeling)
  const [isDurationEditable, setIsDurationEditable] = useState(true);
  // Stores the full dock object of the currently selected dock (for badge display)
  const [selectedDock, setSelectedDock] = useState(null);
  // Filtered door list based on selected dock's IDDock
  const [doorList, setDoorList] = useState([]);

  // --- Conflict / Pre-check state (mirrors BreaksFormSS pattern) ---
  const firstUpdate = useRef(true);
  const [pendingStandingData, setPendingStandingData] = useState(null);
  const [showPreCheckModal, setShowPreCheckModal] = useState(false);
  const [showAutomaticCancelConfirmPopup, setShowAutomaticCancelConfirmPopup] = useState(false);
  const [showAutoMassCancelNotification, setShowAutoMassCancelNotification] = useState(false);

  const ssReducer = useSelector(smartScheduleSelector);
  const user = useSelector(getUserSelector);
  const { cu_id, addCarrierSpro } = user;
  const whMetaData = useSelector(getWhMetaDataReducerSlice);
  const { whMetaDocksList, preCheckList, whMetaTimeslotList } = whMetaData;
  const { standingAppointmentsList, operationObj, carrierList, initateLoading } = ssReducer;
  const [unSlectedDays, setUnSelectedDays] = useState('');
  const [searchableSelectFormData, setSearchableSelectFormData] = useState(null);
  const [dateResetKey, setDateResetKey] = useState(0);

  useEffect(() => {
    if (!_.isEmpty(addCarrierSpro)) {
      dispatch({ type: sagaActions.ADD_CARRIER_SCHEDULE_PRO, payload: { type: 'C' } });
    }

    setLoading(true);
    dispatch({ type: sagaActions.RESET_STANDING_APPOINTMENT_ACTION });
    // Clear any stale preCheckList on mount
    dispatch({ type: sagaActions.UPDATE_PRE_CHECK_LIST, payload: [] });
    setShowAutoMassCancelNotification(false);

    setTimeout(() => {
      fetchStandingAppointments();
    }, [1000]);

    return () => {
      setSearchableSelectFormData(null);
      setSelectedCarrier(null);
      setCarrierListState([]);
      if (!_.isEmpty(addCarrierSpro)) {
        dispatch({ type: sagaActions.ADD_CARRIER_SCHEDULE_PRO, payload: { type: 'C' } });
      }
    };
  }, []);

  // Clean up mass cancel reason list on unmount
  useEffect(() => {
    return () => {
      dispatch({ type: sagaActions.RESET_MASS_CANCEL_REASON });
    };
  }, []);

  useEffect(() => {
    if (_.size(carrierList)) {
      formCarrierListData();
    }
  }, [carrierList]);

  useEffect(() => {
    if (!_.isEmpty(addCarrierSpro)) {
      setCarrierListState([...carrierListState, { value: addCarrierSpro?.carrier_id, label: addCarrierSpro?.carrier_name }]);
      setSelectedCarrier({ value: addCarrierSpro?.carrier_id, label: addCarrierSpro?.carrier_name });
      setSearchableSelectFormData({ value: addCarrierSpro?.carrier_id, label: addCarrierSpro?.carrier_name });
      dispatch({ type: sagaActions.ADD_CARRIER_SCHEDULE_PRO, payload: { type: 'C' } });
    }
  }, [addCarrierSpro]);

  // --- Conflict pre-check effect (mirrors BreaksFormSS) ---
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (preCheckList.length > 0) {
      setShowModal(false);
      setLoading(false);
      const selectedDockId = pendingStandingData?.payload?.IDDock;
      const selectedDock = whMetaDocksList.find(dock => dock.ID === selectedDockId);
      if (selectedDock?.AppointmentCancelationType === 'AUTOMATIC') {
        setShowAutomaticCancelConfirmPopup(true);
      } else {
        setShowPreCheckModal(true);
      }
    } else if (pendingStandingData) {
      // No conflicts - proceed directly with standing appointment creation
      const { payload, operationObj } = pendingStandingData;
      dispatchStandingAppointmentAction(payload, operationObj);
      setPendingStandingData(null);
    }
  }, [preCheckList]);

  useEffect(() => {
    if (operationObj?.type) {
      switch (operationObj?.type) {
        case 'Create Standing Appointments':
        case 'Edit Standing Appointments':
          if (operationObj.status) {
            handleApiResponse(AppConstant.notifications.dataAddedSuccess, operationObj.message);
          } else {
            // Close the form and show a dedicated error popup so the user can go back and fix
            handleApiResponse(AppConstant.commonStrings.failure, operationObj.message);

            // setShowModal(false);
            // setResultErrorMessage(operationObj.message || 'An error occurred. Please try again.');
            // setShowResultErrorModal(true);
            setLoading(false);
            setUnSelectedDays('');
          }
          break;
        case 'Delete Standing Appointments':
          if (operationObj.status) {
            handleApiResponse(AppConstant.notifications.dataAddedSuccess, operationObj.message);
          } else {
            handleApiResponse(AppConstant.commonStrings.failure, operationObj.message);
          }
          break;
        default:
          setLoading(false);
      }
    }
  }, [operationObj]);

  // When all timeslots/doors are fetched, filter by the currently selected dock
  useEffect(() => {
    if (inputs.IDDock && whMetaTimeslotList && whMetaTimeslotList.length) {
      const filtered = whMetaTimeslotList.filter((door) => door.IDDock === inputs.IDDock && door.DoorStatus === true);
      setDoorList(filtered);
    } else {
      setDoorList([]);
    }
  }, [whMetaTimeslotList, inputs.IDDock]);

  const handleApiResponse = (result, message) => {
    if (result === AppConstant.notifications.dataAddedSuccess) {
      showAlert(toast.TYPE.SUCCESS, message, themeSelected);
      setShowModal(false);
      fetchStandingAppointments();
      if (showAutoMassCancelNotification) {
        showAlert(toast.TYPE.INFO, AppConstant.smartScheduler.leaves.massCancellationNotification, themeSelected);
        setShowAutoMassCancelNotification(false);
      }
    } else {
      showAlert(toast.TYPE.ERROR, message, themeSelected);
    }
    setLoading(false);
    setUnSelectedDays('');
  };

  const fetchStandingAppointments = () => {
    setLoading(true);
    const payload = {
      ID: null,
      IDWarehouse: whObj.ID,
      IDDock: null,
      IDCarrier: null,
      StartDateTime: null,
      EndDateTime: null,
      ApptStartDateTime: null,
      DurationMins: null,
      Frequency: null,
      WeeklyData: null,
      RuleProcessingStatus: null,
      IsActive: null,
      IsDeleted: 1,
      CreatedOn: null,
      CreatedBy: null,
      UpdatedOn: null,
      UpdatedBy: null,
      CRUDTYPE: 'S',
    };
    const operationObj = { type: 'Standing Appointments List' };
    dispatch({ type: sagaActions.STANDING_APPOINTMENT_ACTION, payload, operationObj });
  };

  useEffect(() => {
    if (_.size(whMetaDocksList)) {
      setDockList(whMetaDocksList);
    } else {
      setDockList([]);
    }
  }, [whMetaDocksList]);

  const formCarrierListData = () => {
    if (_.size(carrierList)) {
      const carrierData = carrierList.map((el) => ({
        value: el.carrier_id,
        label: el.carrier_name,
      }));
      setCarrierListState([...carrierData]);
    } else {
      setCarrierListState([]);
    }
  };

  const getWeekDayNumber = (name) => {
    const map = { sunday: 1, monday: 2, tuesday: 3, wednesday: 4, thursday: 5, friday: 6, saturday: 7 };
    return map[name];
  };

  const handleOperationalDays = (e) => {
    const { name, checked } = e.target;
    const obj = { ...inputs.WeeklyData, [name]: checked };
    setInputs(inputs => ({ ...inputs, WeeklyData: obj }));
  };

  const fetchDoorList = () => {
    const { apiParams, operationObj } = fetchTimeslotListCommonFunc(whObj);
    dispatch({ type: sagaActions.WAREHOUSE_META_TIMESLOT_ACTION, payload: apiParams, operationObj });
  };

  /**
   * Handles door checkbox toggle for multi-select.
   */
  /**const handleDoorCheckbox = (doorName, checked) => {
    setInputs(prev => {
      
    const current = prev.selectedDoors || [];
    const updated = checked
        ? [...current, doorName]
        : current.filter((name) => name !== doorName);
      return { ...prev, selectedDoors: updated };
    });
    setErrors(prev => ({ ...prev, selectedDoors: '' }));
  };*/

  const handleDoorCheckbox = (doorName, checked) => {
    setInputs(prev => {
      let updated = [];

      if (checked) {
        //  Allow only one checkbox to be selected
        updated = [doorName];
      } else {
        //  If unchecked, clear selection
        updated = [];
      }

      return {
        ...prev,
        selectedDoors: updated,
      };
    });

    //  Clear error once user interacts
    setErrors(prev => ({
      ...prev,
      selectedDoors: '',
    }));
  };

  const handleChange = (data, name, validity) => {
    let value = data;

    // When dock changes, derive DurationMins from CapacityModeling and fetch all doors
    if (name === 'IDDock') {
      const dock = dockList.find((d) => d.ID === data);
      const { durationMins, isDurationEditable: editable } = getDurationFromCapacityModeling(dock || null);
      setIsDurationEditable(editable);
      setSelectedDock(dock || null);
      setDoorList([]);
      setInputs(prev => ({
        ...prev,
        IDDock: data,
        selectedDoors: [],
        // Auto-set DurationMins for TPH/TPM; preserve for Productivity so user can enter
        DurationMins: editable ? (prev.DurationMins || '') : durationMins,
      }));
      // Clear any prior DurationMins / door errors when dock changes
      setErrors(prev => ({ ...prev, DurationMins: '', selectedDoors: '' }));
      // Fetch all doors then filter by IDDock in the useEffect
      if (data) {
        fetchDoorList();
      }
      return;
    }

    if (name === 'DurationMins') {
      const intValue = data ? parseInt(data, 10) : null;
      if (intValue < 0) {
        setErrors(prev => ({ ...prev, DurationMins: 'Frequency cannot be negative.' }));
        value = 0;
      } else if (intValue > 1440) {
        setErrors(prev => ({ ...prev, DurationMins: 'Frequency cannot be greater than 1440 minutes.' }));
        value = 1440;
      } else {
        setErrors(prev => ({ ...prev, DurationMins: '' }));
        value = intValue;
      }
    }

    // AllowOtherCarriersOverSAB — when enabled, force StandingBlockLeadTime to 0 (read-only)
    if (name === 'AllowOtherCarriersOverSAB') {
      const boolValue = data === 'true' || data === true;
      setInputs(prev => ({
        ...prev,
        AllowOtherCarriersOverSAB: boolValue,
        StandingBlockLeadTime: boolValue ? 0 : prev.StandingBlockLeadTime,
      }));
      setErrors(prev => ({ ...prev, StandingBlockLeadTime: '' }));
      return;
    }

    if (name === 'StandingBlockLeadTime') {
      const numValue = data !== '' && data !== null && data !== undefined ? parseInt(data, 10) : '';
      if (numValue !== '' && numValue < 0) {
        setErrors(prev => ({ ...prev, StandingBlockLeadTime: 'Standing lead time cannot be negative.' }));
        value = 0;
      } else {
        setErrors(prev => ({ ...prev, StandingBlockLeadTime: '' }));
        value = numValue;
      }
    }

    // StartDateTime — YYYY-MM-DD string from native date input
    if (name === 'StartDateTime') {
      const newStartStr = data;
      const badInput = validity ? validity.badInput : false;
      const err = validateDateInput(newStartStr, badInput, 'start', todayStr);
      if (err) {
        setErrors(prev => ({ ...prev, StartDateTime: err }));
        setInputs(prev => ({ ...prev, StartDateTime: newStartStr }));
        return;
      }
      // Clear end date if it is before the new start
      if (inputs.EndDateTime && newStartStr && inputs.EndDateTime < newStartStr) {
        setInputs(prev => ({ ...prev, StartDateTime: newStartStr, EndDateTime: '' }));
      } else {
        setInputs(prev => ({ ...prev, StartDateTime: newStartStr }));
      }
      setErrors(prev => ({ ...prev, StartDateTime: '', EndDateTime: '' }));
      return;
    }

    // EndDateTime — YYYY-MM-DD string from native date input
    if (name === 'EndDateTime') {
      const newEndStr = data;
      const badInput = validity ? validity.badInput : false;
      const err = validateDateInput(newEndStr, badInput, 'end', null);
      if (err) {
        setErrors(prev => ({ ...prev, EndDateTime: err }));
        setInputs(prev => ({ ...prev, EndDateTime: newEndStr }));
        return;
      }
      if (inputs.StartDateTime && newEndStr && newEndStr < inputs.StartDateTime) {
        setErrors(prev => ({ ...prev, EndDateTime: 'End date must be on or after start date' }));
        setInputs(prev => ({ ...prev, EndDateTime: newEndStr }));
        return;
      }
      setInputs(prev => ({ ...prev, EndDateTime: newEndStr }));
      setErrors(prev => ({ ...prev, EndDateTime: '' }));
      return;
    }

    // ApptStartDateTime — clear error when user selects a time
    if (name === 'ApptStartDateTime') {
      if (data) {
        setErrors(prev => ({ ...prev, ApptStartDateTime: '' }));
      }
    }

    if (name === 'Frequency') {
      if (value === 'WEEKLY' && inputs.StartDateTime) {
        const endStr = moment(inputs.StartDateTime).add(7, 'days').format('YYYY-MM-DD');
        setInputs(prev => ({ ...prev, [name]: value, EndDateTime: endStr }));
        return;
      }
    }

    if (name === 'ExtendRule') {
      value = data === 'true';
    }

    setInputs(prev => ({ ...prev, [name]: value }));
  };

  const handleDateBlur = (name, validity) => {
    const currentVal = inputs[name] || '';
    const badInput = validity ? validity.badInput : false;
    const fieldRole = name === 'StartDateTime' ? 'start' : 'end';
    const minDate = name === 'StartDateTime' ? todayStr : null;
    const err = validateDateInput(currentVal, badInput, fieldRole, minDate);
    if (err) {
      setErrors(prev => ({ ...prev, [name]: err }));
      return;
    }
    if (name === 'EndDateTime' && inputs.StartDateTime && currentVal && currentVal < inputs.StartDateTime) {
      setErrors(prev => ({ ...prev, EndDateTime: 'End date must be on or after start date' }));
      return;
    }
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  /**
 * Builds the STANDING_APPOINTMENT_ACTION payload and dispatches it.
   * Called either directly (no conflicts) or after conflict resolution.
   */
  const dispatchStandingAppointmentAction = (data, operationObj) => {
    dispatch({ type: sagaActions.STANDING_APPOINTMENT_ACTION, payload: data, operationObj });
  };

  const submitForm = (data) => {
    setLoading(true);

    const startErr = data.StartDateTime
      ? validateDateInput(data.StartDateTime, false, 'start', todayStr)
      : 'This field is required';
    const endErr = data.EndDateTime
      ? validateDateInput(data.EndDateTime, false, 'end', null)
      : 'This field is required';
    const apptStartErr = data.ApptStartDateTime ? '' : 'This field is required';
    const durationMinsErr = (data.DurationMins !== null && data.DurationMins !== undefined && data.DurationMins !== '') ? '' : 'This field is required';
    // At least 1 door required when dock is selected and doors exist
    const doorsErr = (inputs.IDDock && doorList.length > 0 && (!inputs.selectedDoors || inputs.selectedDoors.length === 0))
      ? 'Please select at least one door'
      : '';

    let hasErrors = false;
    const newErrors = { ...errors };

    if (startErr) { newErrors.StartDateTime = startErr; hasErrors = true; }
    if (endErr) { newErrors.EndDateTime = endErr; hasErrors = true; }
    if (data.StartDateTime && data.EndDateTime && !startErr && !endErr && data.EndDateTime < data.StartDateTime) {
      newErrors.EndDateTime = 'End date must be on or after start date';
      hasErrors = true;
    }
    if (apptStartErr) { newErrors.ApptStartDateTime = apptStartErr; hasErrors = true; }
    if (durationMinsErr) { newErrors.DurationMins = durationMinsErr; hasErrors = true; }
    if (doorsErr) { newErrors.selectedDoors = doorsErr; hasErrors = true; }

    if (hasErrors) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    if (!_.isEmpty(selectedCarrier)) {
      const weeklyData =
        data.Frequency === 'WEEKLY'
          ? Object.entries(inputs.WeeklyData)
            .filter(([day, isSelected]) => isSelected)
            .map(([day]) => getWeekDayNumber(day))
          : null;

      if (data.Frequency === 'WEEKLY' && _.isEmpty(weeklyData)) {
        showAlert(toast.TYPE.ERROR, 'Please select at least one operational day for weekly frequency.', themeSelected);
        setLoading(false);
        return;
      }

      const payload = {
        ...data,
        IDCarrier: !_.isEmpty(selectedCarrier) ? selectedCarrier?.value : null,
        DurationMins: Number(data.DurationMins),
        ExtendRule: data.ExtendRule,
        StartDateTime: data.StartDateTime ? moment.utc(data.StartDateTime).toISOString() : null,
        EndDateTime: data.EndDateTime ? moment.utc(data.EndDateTime).toISOString() : null,
        ApptStartDateTime: data.ApptStartDateTime,
        ID: editData.ID || null,
        IDWarehouse: whObj.ID,
        WeeklyData: weeklyData ? JSON.stringify(weeklyData) : null,
        IDDock: inputs['IDDock'],
        StandingBlockTimeSlot: inputs.selectedDoors && inputs.selectedDoors.length > 0 ? inputs.selectedDoors.join(',') : null,
        CreatedOn: changeFormatWithoutTz(getCurrentDate(), AppConstant.commonStrings.YYYY_MM_DD_T_HH_mm_ss),//editData.ID,
        EndTime: data.EndDateTime && data.ApptStartDateTime && data.DurationMins
          ? moment.utc(`${data.EndDateTime}T${data.ApptStartDateTime}:00`).add(Number(data.DurationMins), 'minutes').toISOString()
          : null,
        CreatedBy: editData.ID ? undefined : cu_id,
        UpdatedOn: changeFormatWithoutTz(getCurrentDate(), AppConstant.commonStrings.YYYY_MM_DD_T_HH_mm_ss),
        //editData.ID
        // ? changeFormatWithoutTz(getCurrentDate(), AppConstant.commonStrings.YYYY_MM_DD_T_HH_mm_ss)
        // : undefined,
        UpdatedBy: editData.ID ? cu_id : undefined,
        CRUDTYPE: editData.ID ? 'U' : 'I',
        AppointmentCancelationType: null,
        ConflictAptStatus: false,
        Reason: null,
        ExcludedAppointments: null,
        StandingBlockLeadTime: (data.StandingBlockLeadTime !== '' && data.StandingBlockLeadTime !== null && data.StandingBlockLeadTime !== undefined)
          ? Number(data.StandingBlockLeadTime) * 24
          : 0,
        AllowOtherCarriersOverSAB: inputs.AllowOtherCarriersOverSAB ? true : false,
      };

      const operationObj = {
        type: editData.ID ? 'Edit Standing Appointments' : 'Create Standing Appointments',
        message: editData.ID ? 'Updated successfully' : 'Created successfully',
      };

      // Store pending data for conflict resolution flow
      setPendingStandingData({ payload, operationObj });

      // Run conflict pre-check for both create and edit
      const preCheckPayload = {
        Type: 'STANDING',
        ID: inputs['IDDock'],
        IDWarehouse: whObj.ID,
        StartTime: data.StartDateTime && data.ApptStartDateTime
          ? moment.utc(`${data.StartDateTime}T${data.ApptStartDateTime}:00`).toISOString()
          : null,
        EndTime: data.EndDateTime && data.ApptStartDateTime && data.DurationMins
          ? moment.utc(`${data.EndDateTime}T${data.ApptStartDateTime}:00`).add(Number(data.DurationMins), 'minutes').toISOString()
          : null,
        DockStatus: 1,
        SelectedDays: weeklyData ? weeklyData.join(',') : null,
        IDTimeslot: inputs.selectedDoors && inputs.selectedDoors.length > 0 ? inputs.selectedDoors.join(',') : null,
      };
      dispatch({ type: sagaActions.PRE_CHECK_FOR_ADMIN_OPERATION, payload: preCheckPayload });
    } else {
      setLoading(false);
      console.log('Error: Carrier not selected');
    }
  };

  const onCarrierSelcted = (props) => {
    if (props.length > 2) {
      dispatch({ type: sagaActions.SS_GET_CARRIER_LIST, payload: { carrier: props } });
    }
  };

  const onCreateSelected = (props) => {
    const payload = {
      'name': props,
      'contact_phone': null,
      'first_name': null,
      'last_name': null,
      type: 'A',
    };
    dispatch({ type: sagaActions.ADD_CARRIER_SCHEDULE_PRO, payload });
  };

  const getFormFieldData = (data, name, control, inputs, searchableSelectFormData) => {
    if (name === 'carrier_name') {
      if (searchableSelectFormData) {
        setSelectedCarrier(searchableSelectFormData);
        setSearchableSelectFormData(searchableSelectFormData);
      }
    }
  };

  const formFieldList = [
    {
      name: 'carrier_name',
      type: 'select_with_input_search_add_button_temp',
      size: 'col-sm-12 p-0 f-12',
      parentClass: 'inputTitle',
      componentClassName: ' border border-1 rounded-1 border-dark',
      inputTitle: 'Select Carrier',
      position: 5,
      title: AppConstant.reports.scheduledLoadStatus.tableHeaders.carrierName,
      customStylesLight: customStylesLight,
      placeholderText: 'Select a carrier from the list',
      onInputChange: onCarrierSelcted,
      defaultValue: !_.isEmpty(selectedCarrier) ? selectedCarrier : 'Select a carrier from the list',
      isLoading: false,
      isClearable: false,
      isSearchable: true,
      options: carrierListState,
      formatCreateLabel: () => { },
      loadingMessage: 'Please wait loading....',
      formValidation: { required: true },
      handleCreateOption: onCreateSelected,
      searchableSelectFormData: searchableSelectFormData,
    },
  ];

  const getDockList = () => {
    return dockList.map((ele) => {
      if (ele && ele.DockStatus) {
        const opDays = {
          sunday: ele.sunday, monday: ele.monday, tuesday: ele.tuesday,
          wednesday: ele.wednesday, thursday: ele.thursday, friday: ele.friday, saturday: ele.saturday,
        };
        const dayAbbreviations = {
          sunday: 'Sun', monday: 'Mon', tuesday: 'Tue',
          wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat',
        };
        const activeDays = Object.keys(opDays).filter(d => opDays[d]).map(d => dayAbbreviations[d]);
        const daysDisplay = activeDays.length > 0 ? activeDays.join(', ') : 'Non Operational';
        return (
          <option key={ele.ID} value={ele.ID}>
            {ele.Name} ({daysDisplay})
          </option>
        );
      }
      return null;
    }).filter(Boolean);
  };

  // Compute min end date string (WEEKLY: start+7, else same as start)
  const minEndDateStr = inputs.StartDateTime
    ? (inputs.Frequency === 'WEEKLY'
      ? moment(inputs.StartDateTime).add(7, 'days').format('YYYY-MM-DD')
      : inputs.StartDateTime)
    : todayStr;

  // Compute max end date string (start + 1 year)
  const maxEndDateStr = inputs.StartDateTime
    ? moment(inputs.StartDateTime).add(1, 'years').format('YYYY-MM-DD')
    : '';

  const createForm = () => {
    // Determine if the selected dock's CapacityModeling is TPH or TPM (for badge + disclaimer)
    const capacityModeling = selectedDock ? selectedDock.CapacityModeling : null;
    const showCapacityDisclaimer = capacityModeling === 'TPH' || capacityModeling === 'TPM';

    return (
      <div className='d-flex flex-column gap-2'>
        <CustomFieldWithTtileSS
          type={'select'}
          name={'IDDock'}
          value={inputs['IDDock'] ? inputs['IDDock'] : ''}
          title={'Dock Type'}
          required={true}
          onChange={(data, name) => handleChange(data, name)}
          listData={getDockList}
          disabledField={Object.keys(editData).length ? true : false}
        />

        {/* Capacity Modeling badge — shown when a dock is selected */}
        {capacityModeling && (
          <div className='d-flex align-items-center gap-2'>
            <span className='inputTitle mb-0'>Capacity Model:</span>
            <span
              className='badge rounded-pill'
              style={{
                backgroundColor: capacityModeling === 'Productivity' ? '#198754' : '#0d6efd',
                color: '#fff',
                fontSize: '0.75rem',
                padding: '4px 10px',
              }}
            >
              {getCapacityModelingLabel(capacityModeling)}
            </span>
          </div>
        )}

        {/* Door multi-select checkboxes — shown once a dock is selected */}
        {inputs.IDDock && (
          <div className='d-flex flex-column'>
            <span className='inputTitle'>
              Timeslots&nbsp;<span className='color-red'>*</span>
            </span>
            {doorList && doorList.length > 0 ? (
              <div className='checkbox-group'>
                <div className='flex-wrap'>
                  {doorList.map((door) => (
                    <div key={door.ID} className='d-flex gap-1'>
                      <input
                        type='checkbox'
                        id={`door-${door.ID}`}
                        checked={inputs.selectedDoors ? inputs.selectedDoors.includes(door.TimeSlotName) : false}
                        onChange={(e) => handleDoorCheckbox(door.TimeSlotName, e.target.checked)}
                      />
                      <label htmlFor={`door-${door.ID}`}>{door.TimeSlotName}</label>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <span className='subText'>No timeslots available for selected dock</span>
            )}
            {errors.selectedDoors && (
              <span className='errorMessage'>{errors.selectedDoors}</span>
            )}
            <div className='my-1 subText'>
              <span className='color-red pe-1'>*</span>
              <span>Only one timeslot must be selected.</span>
            </div>
          </div>
        )}

        <CustomDynamicFormSS
          formFieldList={formFieldList}
          sort={true}
          themeSelected={themeSelected}
          getFormFieldData={getFormFieldData}
          loading={loading}
          disclaimer={false}
          disclaimersArray={[]}
          customDisableSave={false}
        />
        <CustomFieldWithTtileSS
          type={'select'}
          name='Frequency'
          listData={() => ([<option value="DAILY">Daily</option>, <option value="WEEKLY">Weekly</option>])}
          value={inputs.Frequency || ''}
          title='Frequency'
          required={true}
          onChange={(data, name) => handleChange(data, name)}
        />
        {inputs.Frequency === 'WEEKLY' &&
          <div className='checkbox-group'>
            <label>Operational days <span className='color-red'>*</span></label>
            <div className='flex-wrap'>
              <div className='d-flex gap-1'>
                <input type='checkbox' name='sunday' onChange={handleOperationalDays} checked={inputs?.WeeklyData?.sunday} />
                <span>Sunday</span>
              </div>
              <div className='d-flex gap-1'>
                <input type='checkbox' name='monday' onChange={handleOperationalDays} checked={inputs?.WeeklyData?.monday} />
                <span>Monday</span>
              </div>
              <div className='d-flex gap-1'>
                <input type='checkbox' name='tuesday' onChange={handleOperationalDays} checked={inputs?.WeeklyData?.tuesday} />
                <span>Tuesday</span>
              </div>
              <div className='d-flex gap-1'>
                <input type='checkbox' name='wednesday' onChange={handleOperationalDays} checked={inputs?.WeeklyData?.wednesday} />
                <span>Wednesday</span>
              </div>
              <div className='d-flex gap-1'>
                <input type='checkbox' name='thursday' onChange={handleOperationalDays} checked={inputs?.WeeklyData?.thursday} />
                <span>Thursday</span>
              </div>
              <div className='d-flex gap-1'>
                <input type='checkbox' name='friday' onChange={handleOperationalDays} checked={inputs?.WeeklyData?.friday} />
                <span>Friday</span>
              </div>
              <div className='d-flex gap-1'>
                <input type='checkbox' name='saturday' onChange={handleOperationalDays} checked={inputs?.WeeklyData?.saturday} />
                <span>Saturday</span>
              </div>
            </div>
          </div>
        }

        {/* Start Date */}
        <div className='d-flex flex-column'>
          <span className='inputTitle'>
            Start Date&nbsp;<span className='color-red'>*</span>
          </span>
          <input
            key={`StartDateTime-${dateResetKey}`}
            type="date"
            className={`customTextInput w-100${errors.StartDateTime ? ' customError' : ''}`}
            value={inputs.StartDateTime || ''}
            min={todayStr}
            max="9999-12-31"
            onChange={(e) => handleChange(e.target.value, 'StartDateTime', e.target.validity)}
            onBlur={(e) => handleDateBlur('StartDateTime', e.target.validity)}
          />
          {errors.StartDateTime && (
            <span className='errorMessage'>{errors.StartDateTime}</span>
          )}
        </div>

        {/* End Date */}
        <div className='d-flex flex-column'>
          <span className='inputTitle'>
            End Date&nbsp;<span className='color-red'>*</span>
          </span>
          <input
            key={`EndDateTime-${dateResetKey}`}
            type="date"
            className={`customTextInput w-100${errors.EndDateTime ? ' customError' : ''}`}
            value={inputs.EndDateTime || ''}
            min={minEndDateStr}
            max={maxEndDateStr || '9999-12-31'}
            disabled={!inputs.StartDateTime}
            onChange={(e) => handleChange(e.target.value, 'EndDateTime', e.target.validity)}
            onBlur={(e) => handleDateBlur('EndDateTime', e.target.validity)}
          />
          {errors.EndDateTime && (
            <span className='errorMessage'>{errors.EndDateTime}</span>
          )}
        </div>

        <div className='d-flex flex-column'>
          <CustomFieldWithTtileSS
            type={'custom-time-selector'}
            name={'ApptStartDateTime'}
            value={inputs.ApptStartDateTime || ''}
            title={'Appointment Start Time'}
            required={true}
            onChange={(data, name) => handleChange(data, name)}
            customDatePickerConfig={{
              selected: inputs.ApptStartDateTime ? moment(inputs.ApptStartDateTime, 'h:mm a').toDate() : null,
              name: 'ApptStartDateTime',
              showTimeSelectOnly: true,
              showTimeSelect: true,
              timeFormat: 'HH:mm',
              timeIntervals: 15,
              timeCaption: 'Time',
              dateFormat: 'h:mm aa',
            }}
          />
          {errors.ApptStartDateTime && (
            <span className='errorMessage'>{errors.ApptStartDateTime}</span>
          )}
        </div>

        <div className='d-flex flex-column'>
          <CustomFieldWithTtileSS
            type={'number'}
            name={'DurationMins'}
            value={inputs['DurationMins'] !== null && inputs['DurationMins'] !== undefined ? inputs['DurationMins'] : ''}
            title={'Duration (min)'}
            required={true}
            onChange={(data, name) => handleChange(data, name)}
            disabledField={!isDurationEditable}
          />
          {errors.DurationMins && (
            <span className='errorMessage'>{errors.DurationMins}</span>
          )}
          {/* Info disclaimer shown only for TPH / TPM capacity models */}
          {showCapacityDisclaimer && (
            <div className="my-3 subText color-red">
              <div className="row">
                <div className="col-12">
                  <span className="color-red pe-1">*</span>
                  <span>
                    Duration is set automatically based on the dock's capacity model (
                    <strong>{getCapacityModelingLabel(capacityModeling)}</strong>
                    ) and cannot be edited.
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bypass for Other Carriers toggle (Phase 2 SAB) */}
        <CustomFieldWithTtileSS
          name='AllowOtherCarriersOverSAB'
          type={'radio'}
          required={true}
          title={
            <span>
              Bypass for Other Carriers&nbsp;
              <i
                className="fa fa-info-circle"
                aria-hidden="true"
                data-for="bypass-carriers-tooltip"
                data-tip="bypass-carriers-tooltip"
              />
            </span>
          }
          value={String(inputs['AllowOtherCarriersOverSAB'] ?? false)}
          radiconfig={[{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }]}
          className='col-sm-12 mt-2 radioButtonText'
          onChange={(data, name) => handleChange(data, name)}
        />
        <CustomTooltip
          id={'bypass-carriers-tooltip'}
          title="When enabled, the SAB slot acts as a dedicated extra appointment. Other carriers can still book the regular capacity slots. Standing Block Lead Time is not applicable and will be set to 0."
          position='top'
          type={themeSelected === AppConstant.commonStrings.lightModeFilterString ? 'dark' : 'light'}
          multiline={true}
        />

        <div className='d-flex flex-column'>
          <CustomFieldWithTtileSS
            type={'number'}
            name={'StandingBlockLeadTime'}
            value={inputs['StandingBlockLeadTime'] !== null && inputs['StandingBlockLeadTime'] !== undefined ? inputs['StandingBlockLeadTime'] : ''}
            title={<span>Standing Block Lead Time (in days) <i className="fa fa-info-circle" aria-hidden="true" data-for="standing-lead-time-tooltip" data-tip="standing-lead-time-tooltip"></i></span>}
            required={false}
            onChange={(data, name) => handleChange(data, name)}
            disabledField={inputs.AllowOtherCarriersOverSAB === true}
          />
          {errors.StandingBlockLeadTime && (
            <span className='errorMessage'>{errors.StandingBlockLeadTime}</span>
          )}
          {inputs.AllowOtherCarriersOverSAB && (
            <div className='my-1 subText'>
              <span className='color-red pe-1'>*</span>
              <span>Lead time is not applicable when "Bypass for Other Carriers" is enabled and is fixed at 0.</span>
            </div>
          )}
          <CustomTooltip
            id={'standing-lead-time-tooltip'}
            title="Bookings close {X} days before the SAB date. Unbooked slots will release to other carriers for booking."
            position='top'
            type={themeSelected === AppConstant.commonStrings.lightModeFilterString ? 'dark' : 'light'}
            multiline={true}
          />
        </div>

        <CustomFieldWithTtileSS
          name='ExtendRule'
          type={'radio'}
          required={true}
          title={'Auto Extend Rule'}
          value={String(inputs['ExtendRule'] ?? false)}
          radiconfig={[{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }]}
          className='col-sm-12 mt-2 radioButtonText'
          onChange={(data, name) => handleChange(data, name)}
        />

        <div className='d-flex justify-content-end my-2'>
          <CustomButtonSS
            isLoading={false}
            title={'Reset'}
            className={'btn-unstyled'}
            onClick={() => {
              setInputs({ IDDock: null, selectedDoors: [], Frequency: 'DAILY', IsActive: true, StartDateTime: '', EndDateTime: '', StandingBlockLeadTime: '', AllowOtherCarriersOverSAB: false });
              setSearchableSelectFormData(null);
              setSelectedCarrier(null);
              setSelectedDock(null);
              setDoorList([]);
              setErrors({});
              setIsDurationEditable(true);
              setDateResetKey((k) => k + 1);
            }}
          />
          <CustomButtonSS
            isLoading={loading}
            title={'Save'}
            className={'btn-styled'}
            onClick={() => submitForm(inputs)}
          />
        </div>
      </div>
    );
  };

  const fetchDockList = () => {
    const { apiParams, operationObj } = fetchDockListCommonFunc(whObj);
    dispatch({ type: sagaActions.WAREHOUSE_META_DOCK_LIST_ACTION, payload: apiParams, operationObj: operationObj });
  };

  const openCreateForm = () => {
    setShowModal(true);
    setEditData({});
    setInputs({
      IDCarrier: null,
      IDDock: null,
      selectedDoors: [],
      Frequency: 'DAILY',
      IsActive: true,
      WeeklyData: {
        sunday: false, monday: false, tuesday: false, wednesday: false,
        thursday: false, friday: false, saturday: false,
      },
      ExtendRule: false,
      StartDateTime: '',
      EndDateTime: '',
      StandingBlockLeadTime: '',
      AllowOtherCarriersOverSAB: false,
    });
    setIsDurationEditable(true);
    setSelectedDock(null);
    setDoorList([]);
    fetchDockList();
    setErrors({});
    setSearchableSelectFormData(null);
    setSelectedCarrier(null);
  };

  const closeCurrentpopup = () => {
    setEditData({});
    setErrors({});
    setShowModal(false);
  };

  const showCreateStandingPopup = () => {
    const popupObj = {
      id: 'createStandingAppointment',
      modalLabel: 'createStandingAppointment',
      showClose: true,
      showfooter: false,
      showHeader: true,
      sendDriverNotification: false,
      modalSize: 'md',
      title: !Object.keys(editData).length ? 'Create Standing Appointment Block' : 'Edit Standing Appointment Block',
      bodyText: createForm(),
      subText: '',
      keyboard: false,
      backdrop: 'static',
    };
    return <SSPopup {...popupObj} closepopup={closeCurrentpopup} themeSelected={themeSelected} showModal={showModal} />;
  };

  const handleEdit = (appt) => {
    const weeklyData = appt.WeeklyData ? {
      sunday: appt.WeeklyData.includes(7),
      monday: appt.WeeklyData.includes(1),
      tuesday: appt.WeeklyData.includes(2),
      wednesday: appt.WeeklyData.includes(3),
      thursday: appt.WeeklyData.includes(4),
      friday: appt.WeeklyData.includes(5),
      saturday: appt.WeeklyData.includes(6),
    } : {
      sunday: false, monday: false, tuesday: false, wednesday: false,
      thursday: false, friday: false, saturday: false,
    };

    // Derive editability from the appointment's CapacityModeling
    const apptDockProxy = appt.CapacityModeling
      ? { CapacityModeling: appt.CapacityModeling, TPM: appt.TPM }
      : null;
    const { isDurationEditable: editable } = getDurationFromCapacityModeling(apptDockProxy);
    setIsDurationEditable(editable);
    // Set selectedDock proxy so the badge and disclaimer render correctly in edit mode
    setSelectedDock(apptDockProxy);

    // Parse stored StandingBlockTimeSlot (comma-separated TimeSlotNames) back to array of strings
    const parsedSelectedDoors = appt.StandingBlockTimeSlot
      ? appt.StandingBlockTimeSlot.toString().split(',').map((name) => name.trim())
      : [];

    const editObject = {
      ...appt,
      StartDateTime: appt.StartDateTime ? moment.utc(appt.StartDateTime).format('YYYY-MM-DD') : '',
      EndDateTime: appt.EndDateTime ? moment.utc(appt.EndDateTime).format('YYYY-MM-DD') : '',
      ApptStartDateTime: appt.ApptStartDateTime
        ? moment.utc(appt.ApptStartDateTime).format('HH:mm')
        : '',
      WeeklyData: weeklyData,
      ExtendRule: appt.ExtendRule,
      selectedDoors: parsedSelectedDoors,
      StandingBlockLeadTime: appt.StandingBlockLeadTime !== null && appt.StandingBlockLeadTime !== undefined ? appt.StandingBlockLeadTime / 24 : '',
      AllowOtherCarriersOverSAB: appt.AllowOtherCarriersOverSAB ? true : false,
    };

    setInputs(editObject);
    setEditData(editObject);
    setErrors({});

    // Pre-populate carrier from appointment data
    if (appt.IDCarrier && appt.CarrierName) {
      const carrierObj = { value: appt.IDCarrier, label: appt.CarrierName };
      setSelectedCarrier(carrierObj);
      setSearchableSelectFormData(carrierObj);
    }

    // Fetch dock list so the disabled dock dropdown renders the correct option
    fetchDockList();

    // Fetch doors for the dock of this appointment
    if (appt.IDDock) {
      fetchDoorList();
    }

    setShowModal(true);
  };

  const handleDelete = (id) => {
    const payload = {
      ID: id,
      IDWarehouse: whObj.ID,
      CRUDTYPE: 'D',
      UpdatedOn: changeFormatWithoutTz(getCurrentDate(), AppConstant.commonStrings.YYYY_MM_DD_T_HH_mm_ss),
      UpdatedBy: cu_id ? cu_id : 'Not Found',
    };
    const operationObj = {
      type: 'Delete Standing Appointments',
      message: 'Deleted successfully',
    };
    dispatch({ type: sagaActions.STANDING_APPOINTMENT_ACTION, payload, operationObj });
  };

  // --- Conflict popup handlers (mirrors BreaksFormSS) ---

  const closePreCheckpopup = () => {
    setShowPreCheckModal(false);
  };

  const popupBtnHandler = (text) => {
    if (text === AppConstant.commonStrings.viewDetailsStr) {
      navigate('/manageappointments');
    } else {
      setShowPreCheckModal(false);
    }
  };

  const showPreCheck = () => {
    const popupObj = {
      id: 'adminUpdateOperationPreCheck',
      modalLabel: 'adminUpdateOperationPreCheck',
      showClose: true,
      showfooter: true,
      showHeader: true,
      sendDriverNotification: false,
      modalSize: 'md',
      title: AppConstant.smartScheduler.leaves.apptConflict,
      bodyText: AppConstant.superUserAdmin.warehouseAdmin.preCheckBockerMsg,
      subText: '',
      keyboard: false,
      backdrop: 'static',
      footerBtn: {
        btn1: { show: true, text: AppConstant.commonStrings.viewDetailsStr },
        btn2: { show: false },
      },
    };
    return (
      <SSPopup
        {...popupObj}
        popupBtnHandler={(text) => { popupBtnHandler(text); }}
        closepopup={() => { closePreCheckpopup(); }}
        themeSelected={themeSelected}
        showModal={showPreCheckModal}
      />
    );
  };

  const handleAutomaticCancelConfirm = (selectedReason, excludedAppointments) => {
    setLoading(true);
    if (pendingStandingData) {
      const { payload, operationObj } = pendingStandingData;
      // If selectedReason is null, all appointments were excluded - skip cancellation, still create the SAB
      const allExcluded = !selectedReason;
      const newPayload = {
        ...payload,
        ConflictAptStatus: !allExcluded,
        AppointmentCancelationType: allExcluded ? null : 'AUTOMATIC',
        Reason: allExcluded ? null : selectedReason,
        ExcludedAppointments: !allExcluded && excludedAppointments && excludedAppointments.length
          ? excludedAppointments.toString()
          : null,
      };
      dispatch({ type: sagaActions.UPDATE_PRE_CHECK_LIST, payload: [] });
      dispatchStandingAppointmentAction(newPayload, operationObj);
      setPendingStandingData(null);
      if (!allExcluded) {
        setShowAutoMassCancelNotification(true);
      }
    }
  };

  const showAutomaticCancelPopup = () => {
    if (!showAutomaticCancelConfirmPopup) return null;

    const startDisplay = pendingStandingData?.payload?.StartDateTime
      ? moment(pendingStandingData.payload.StartDateTime.split('T')[0], 'YYYY-MM-DD').format('MM/DD/YYYY')
      : '';
    const endDisplay = pendingStandingData?.payload?.EndDateTime
      ? moment(pendingStandingData.payload.EndDateTime.split('T')[0], 'YYYY-MM-DD').format('MM/DD/YYYY')
      : '';
    const uniqueCount = _.uniqBy(preCheckList, 'PORelationID').length;
    const popupBodyText = `You have scheduled a standing appointment from ${startDisplay} to ${endDisplay}. There ${uniqueCount === 1 ? 'is ' : 'are '} ${uniqueCount} appointment${uniqueCount !== 1 ? 's' : ''} already scheduled for the following time slot(s):`;

    return (
      <AutomaticCancelPopup
        themeSelected={themeSelected}
        showPopup={showAutomaticCancelConfirmPopup}
        setShowPopup={setShowAutomaticCancelConfirmPopup}
        pendingData={pendingStandingData}
        setPendingData={setPendingStandingData}
        whObj={whObj}
        onProceed={handleAutomaticCancelConfirm}
        popupTitle="Confirm Standing Appointment"
        popupBodyText={popupBodyText}
        cuid={cu_id}
        showCheckboxes={true}
      />
    );
  };

  return (
    <div>
      <div className='container-fluid d-flex py-2 text-start justify-content-between mt-1'>
        <button
          className={'btn-highlighted f-12 me-3'}
          data-bs-toggle="modal"
          onClick={() => openCreateForm()}>
          {'Create Standing Appointment Block'}
        </button>
        <div className='d-flex align-items-center ms-5'>
          <CustomButtonSS
            disabled={false}
            isLoading={false}
            className={`carrier-po-btn-small`}
            onClick={fetchStandingAppointments}
            isIcon={true}
            iconClass={'fa-solid fa-rotate-right'}
          />
          <WarehouseSearchSS
            placeholder={'Search'}
            inputValue={searchStr}
            cancelSearch={() => setSearchStr('')}
            onChangeHandler={(text) => setSearchStr(text)} />
        </div>
      </div>
      <div className="d-flex justify-content-center metaDockList">
        <StandingAppointmentsList
          standingAppointmentsList={standingAppointmentsList}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          themeSelected={themeSelected}
          whObj={whObj}
          searchStr={searchStr}
          isListLoading={initateLoading}
        />
      </div>
      {showCreateStandingPopup()}
      {showPreCheck()}
      {showAutomaticCancelPopup()}
    </div>
  );
};

export default StandingAppointmentsBlock;
